import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { Scene } from '@babylonjs/core/scene';
import { MeshParser } from '@seddi/mesh';
import type { Button } from '@seddi/scene';
import { MeshExt, geometryCreate } from '@seddi/ui-babylon-core';
import { ButtonAdapter } from './buttonAdapter';
import type { IBabylonButton } from './buttonTypes';
import type { SurfaceMaterialLoader } from './surfaceMaterialLoader';

/**
 * Loads a button.
 */
export class ButtonLoader {
  private scene: Scene;
  private surfaceMaterialLoader: SurfaceMaterialLoader;

  constructor(scene: Scene, surfaceMaterialLoader: SurfaceMaterialLoader) {
    this.scene = scene;
    this.surfaceMaterialLoader = surfaceMaterialLoader;
  }

  /**
   * Loads a button.
   * @param data - Button to load.
   */
  async loadAsync(data: Button): Promise<IBabylonButton> {
    if (!data.geometryData) {
      // TODO: FileLoader used for the button mesh file download
      const response = await fetch(data.geometry);
      const buffer = await response.arrayBuffer();
      const parsed = MeshParser.Parse(buffer);
      data.geometryData = parsed.geometry;
    }

    const desc = ButtonAdapter.adapt(data);
    const babylonGeometry = geometryCreate(desc.id, this.scene, desc.geometry);
    const babylonMaterial = await this.surfaceMaterialLoader.loadAsync(desc.material);
    const babylonMesh = new Mesh(desc.id, this.scene);
    MeshExt.configure(babylonMesh);
    babylonGeometry.applyToMesh(babylonMesh);
    babylonMesh.isVisible = false;
    babylonMesh.material = babylonMaterial;
    const button: IBabylonButton = {
      geometry: babylonGeometry,
      material: babylonMaterial,
      mesh: babylonMesh,
    };
    return button;
  }

  /**
   * Unloads a button.
   * @param scene - Babylon scene.
   * @param renderable - Button to unload.
   */
  // eslint-disable-next-line @typescript-eslint/require-await -- Todo
  async unload(renderable: IBabylonButton) {
    this.scene.removeMesh(renderable.mesh);
    this.scene.removeMaterial(renderable.material);
  }
}
